import { type Location } from "../model/types"
import { grid_to_plane } from "./coords"
import { BOX_WIDTH } from "./grid"
import { type PixiScene } from "./pixi_scene"
import { drawBoxHighlightTo, renderOpenSquareTo } from "./renderers"

export type PlaneRectangle = {
  px: number
  py: number
  width: number
  height: number
}

export type OpenCellOverlay = {
  location: Location
  width: number
  height: number
  is_dark_mode: boolean
}

export type LassoBox = {
  gx: number
  gy: number
  width: number
  height: number
}

export default class InteractionOverlayRenderer {
  private scene: PixiScene
  private pointer_square_size: { width: number; height: number } | null = null

  constructor(scene: PixiScene) {
    this.scene = scene
  }

  set_hover_visible(is_visible: boolean): void {
    this.scene.layers.hover.renderable = is_visible
  }

  // The pointer square is shown or hidden on every pointer move, including the
  // ones Pixi repeats while the pointer rests on the canvas. These return
  // whether anything changed, so those don't redraw the canvas.
  hide_pointer_square(): boolean {
    if (!this.scene.pointer_square.renderable) return false

    this.scene.pointer_square.renderable = false
    return true
  }

  show_pointer_square({ px, py, width, height }: PlaneRectangle): boolean {
    const graphics = this.scene.pointer_square
    const size = this.pointer_square_size
    if (size?.width !== width || size?.height !== height) {
      graphics.clear()
      drawBoxHighlightTo(graphics, width, height, 0xffaaaa)
      this.pointer_square_size = { width, height }
    } else if (graphics.renderable && graphics.x === px && graphics.y === py) {
      return false
    }
    graphics.x = px
    graphics.y = py
    graphics.renderable = true
    return true
  }

  render_open_cell_overlay({
    location: { gx, gy },
    width,
    height,
    is_dark_mode,
  }: OpenCellOverlay): void {
    const opened_layer = this.scene.layers.opened
    opened_layer.x = gx * BOX_WIDTH
    opened_layer.y = gy * BOX_WIDTH
    renderOpenSquareTo(opened_layer, is_dark_mode, width, height)
    opened_layer.renderable = true
  }

  hide_open_cell_overlay(): void {
    this.scene.layers.opened.renderable = false
  }

  show_lasso({ gx, gy, width, height }: LassoBox): void {
    const lasso_layer = this.scene.layers.lasso
    lasso_layer.clear()

    if (width < 2 && height < 2) return

    drawBoxHighlightTo(lasso_layer, width, height, 0x3355aa)
    const { px, py } = grid_to_plane({ gx, gy })
    lasso_layer.x = px
    lasso_layer.y = py
    lasso_layer.renderable = true
  }

  hide_lasso(): void {
    this.scene.layers.lasso.renderable = false
  }
}
